import { grey } from '@mui/material/colors';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  ChartDataset,
  TooltipItem,
  ChartOptions,
} from 'chart.js';
import { CONVERSION_COLORS } from 'pages/Monitoring/OrderConversion/OrderConversionList/constants/conversionColors';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { TranslationKey } from 'providers/i18n/types';
import { Bar } from 'react-chartjs-2';
import { TooltipWithInfoIcon } from 'shared/mui/Tooltip';
import { ConversionItem, TotalOrdersGroups } from 'types/embedded/orderConversion';

import {
  CardContentStyled,
  StyledCard,
  StyledCardHeader,
} from './OrderConversionListConversionChart.styled';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type Dataset = ChartDataset<'bar', number[]> & {
  total: number;
};
type TooltipContext = Omit<TooltipItem<'bar'>, 'dataset'> & { dataset: Dataset };

const options: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
    },
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: function ({ dataset }: TooltipContext) {
          return `${dataset.data[0]}% / ${dataset.total}`;
        },
      },
      backgroundColor: grey[700],
      yAlign: 'top',
    },
  },
};

type Props = {
  items: ConversionItem[];
  totalGroups: TotalOrdersGroups;
};

export const OrderConversionListConversionChart: React.FC<Props> = ({ items, totalGroups }) => {
  const translate = useTypedTranslate();

  const datasets = items.map((item) => {
    const totalKey = Object.keys(totalGroups).find((key) => key.includes(item.label));
    const totalValue = totalKey ? totalGroups[totalKey as keyof TotalOrdersGroups] : 0;

    return {
      label: translate(`app.statuses.${[item.label]}` as TranslationKey),
      data: [item.value],
      total: totalValue,
      backgroundColor: CONVERSION_COLORS[item.label],
    };
  });

  const data: ChartData<'bar', number[], string> = {
    labels: [''],
    datasets,
  };

  return (
    <StyledCard>
      <StyledCardHeader
        title={
          <>
            {translate('app.orderConversion.conversion')}{' '}
            <TooltipWithInfoIcon
              iconSize={20}
              tooltipText={translate('app.orderConversion.overallConversionForSelectedGateways')}
            />
          </>
        }
        titleSize="smallBold"
      />
      <CardContentStyled>
        <Bar data={data} options={options} />
      </CardContentStyled>
    </StyledCard>
  );
};
